// TS1(2) — THE PER-TICKET VERIFICATION PASS, AS LAW.
//
// HER CLAUSE, VERBATIM: *"a per-ticket VERIFICATION PASS reading that member's real state (account
// row, tier and grants, subscription and beacon chains, heal verdicts, gate/fault rows for their id,
// generation history, held content, the known-issue classes) that confirms whether the issue is REAL
// and names the cause plainly."*
//
// **THREE VERDICTS AND NOT TWO, AND THE THIRD IS THE ROUND.** `confirmed` and `not-reproducible` are
// the two anybody would write; `cannot-tell` is the one that must be NAMED rather than folded into
// either (R18/R143/R160, and DT-1c's `unknown` cause one instrument over). An unmeasured lane is not
// a clean one — and this is the surface where that mistake costs a member being told her real
// problem is not real, in her own service history, forever.
//
// **NO CAUSE IS INVENTED. EVERY ONE IS READ OFF A ROSTER (R31), AND THE PUSH CLASS READS THE ROSTER
// THIS HOUSE ALREADY SHIPS.** `DELIVERY_CAUSES` publishes exactly the shape a named cause wants
// (`key` / `label` / `why` / `act`), so the notification class does not restate a word of it — it is
// the roster, by reference. The other three classes declare their own in that same shape, held as a
// biconditional against the class list, and `causeFor` REFUSES a key nobody rostered rather than
// composing a plausible sentence about it (R141: refuse, never assume).
//
// **THIS IS OWNER-FACING AND THAT IS WHY IT MAY EXIST AT ALL.** S5's ban is that what a MEMBER is
// TOLD is hers; a verification lands in her dashboard under the member's name and is read by her.
// Nothing here composes a sentence anybody sends to anybody.
//
// It imports ONE module — `./deliveryVerdict.js`, which imports nothing — so a gate loads and RUNS
// every line of this file with no database, no clock and no member.
import { DELIVERY_CAUSES, memberDelivery } from "./deliveryVerdict.js";

// ---------------------------------------------------------------------------
// THE THREE VERDICTS. Ranked, because the roll-up below is that ranking.
// ---------------------------------------------------------------------------
export const VERDICTS = [
  {
    key: "confirmed",
    label: "Real",
    why: "Her own rows show the thing she wrote in about. This is not a teaching.",
    rank: 3,
  },
  {
    key: "cannot-tell",
    label: "Cannot tell",
    // The load-bearing one. A lane nobody could read is not a lane that came back clean, and the
    // difference is the whole reason this verdict exists rather than the two obvious ones.
    why: "Something this pass had to read was not there. Not a clean bill — a lane nobody could look down.",
    rank: 2,
  },
  {
    key: "not-reproducible",
    label: "Not reproducible",
    // A TEACHING, never a denial (her own word for it). It says her state does not show this today.
    why: "Everything this pass could read came back healthy for her. A teaching, never a denial.",
    rank: 1,
  },
];
export const VERDICT_KEYS = VERDICTS.map((v) => v.key);
export const verdictValid = (k) => VERDICT_KEYS.includes(String(k || ""));
export const verdictFor = (k) => VERDICTS.find((v) => v.key === String(k || "")) || null;
const rankOf = (k) => verdictFor(k)?.rank ?? 0;

// ---------------------------------------------------------------------------
// THE CAUSE ROSTERS FOR THE THREE CLASSES THIS HOUSE HAS NO PUBLISHED ROSTER FOR.
//
// Same four fields as `DELIVERY_CAUSES`, deliberately, so a rendering never has to ask which class a
// cause came from. Each roster carries an `unrecorded` member: a check that reaches `cannot-tell`,
// or a `confirmed` whose evidence cannot name why, still names something honest rather than nothing.
// ---------------------------------------------------------------------------
export const FAULT_CAUSES = [
  {
    key: "room-fault",
    label: "A handler threw for her",
    // D-1/D-4's receipt. `gate_log` under `living-room-fault` is written by the one `try` above all
    // 29 Living Room routes, with the route, the error and her id — the whole reason that row exists
    // is that "it will not delete" used to leave nothing to look up.
    why: "The room threw while she was using it, and the row says which route and what the error was.",
    act: "Ours. Read the route and the error off the fault row and trace from the surface she named.",
  },
  {
    key: "unrecorded",
    label: "Cause not recorded",
    why: "Nothing readable says why. A cause named wrongly is more expensive than one not named.",
    act: "Nothing to act on from this lane yet.",
  },
];

export const ACCESS_CAUSES = [
  {
    key: "paid-not-recognised",
    label: "She paid and the house does not know",
    // The one access fault this pass can decide without guessing which door she meant: her paid
    // record says one thing and what the house resolved for her says another. That is a real defect
    // whichever way it is fixed, and it is the shape L2.7 was.
    why: "Her paid record and the entitlements the house resolved for her disagree, so doors are shut that she has bought.",
    act: "Ours. Read her subscription row against `resolveEntitlements`, and drop her cached entitlements.",
  },
  {
    key: "unrecorded",
    label: "Cause not recorded",
    why: "Nothing readable says why. A cause named wrongly is more expensive than one not named.",
    act: "Nothing to act on from this lane yet.",
  },
];

export const HELD_CAUSES = [
  {
    key: "content-held",
    label: "The room is holding her words",
    // LR1 ruling 1. Shadow-hide is invisible BY DESIGN — she sees her own post and nobody else does,
    // and she is deliberately told nothing — so "nobody can see my post" is the one class of ticket
    // where the member cannot possibly have the evidence and the house always can.
    why: "Content of hers is held, so she can see it and nobody else can. That is the room working, and it is why nobody answered her.",
    act: "Yours. The hold and its reason are in the room's queue, where lifting it is one tap.",
  },
  {
    key: "unrecorded",
    label: "Cause not recorded",
    why: "Nothing readable says why. A cause named wrongly is more expensive than one not named.",
    act: "Nothing to act on from this lane yet.",
  },
];

// ---------------------------------------------------------------------------
// THE CLASSES. Each names WHAT IT READS, its cause roster, and the cause it falls back to when the
// evidence cannot say — and `unrecorded` must be a key on that class's OWN roster, which is held.
//
// **THE WORDS ARE A FOCUS, NEVER A FILTER.** They decide which class the HEADLINE verdict is about;
// every class is still checked and still reported. A ticket matching nothing focuses on ALL of them
// (R19's direction, stated rather than implied): a class that ran and found nothing costs one line in
// her report, while a class that never ran reports a member's real problem as not-reproducible.
// ---------------------------------------------------------------------------
export const VERIFICATION_CLASSES = [
  {
    key: "push",
    label: "Notifications",
    reads: "Her beacon chains, and what the newest send says about the endpoint on her phone.",
    causes: DELIVERY_CAUSES,
    unrecorded: "unknown",
    words: /\b(?:notification|notifications|notified|notify|alert|alerts|push|badge|bell|ping|buzz|nudge|lock ?screen|reminder)\b/i,
  },
  {
    key: "fault",
    label: "The room",
    reads: "Fault rows filed under her id when a Living Room handler threw.",
    causes: FAULT_CAUSES,
    unrecorded: "unrecorded",
    words: /\b(?:living ?room|community|post|posts|posted|comment|comments|message|messages|dm|thread|delete|deleting|reply|replies|feed|error|crash(?:ed|es|ing)?|unavailable)\b/i,
  },
  {
    key: "access",
    label: "Access",
    reads: "Her paid record beside the entitlements the house resolved for her.",
    causes: ACCESS_CAUSES,
    unrecorded: "unrecorded",
    words: /\b(?:locked|lock|access|upgrade|upgraded|membership|member|tier|newbie|apprentice|vet|spirit ?keeping|subscri\w*|paid|pay|premium|room|rooms|open|opens|can'?t get in|cannot get in|shut out|paywall)\b/i,
  },
  {
    key: "held",
    label: "Held content",
    reads: "Her own rows the room is holding, and the reason recorded with each.",
    causes: HELD_CAUSES,
    unrecorded: "unrecorded",
    words: /\b(?:nobody (?:can )?see|no ?one (?:can )?see|invisible|not showing|no(?:t)? visible|hidden|shadow|disappeared|vanished|nobody replied|no ?one replied|nobody answers|no likes)\b/i,
  },
];
export const VERIFICATION_CLASS_KEYS = VERIFICATION_CLASSES.map((c) => c.key);
export const verificationClass = (k) =>
  VERIFICATION_CLASSES.find((c) => c.key === String(k || "")) || null;

// A cause, by class and key. **REFUSES rather than composes** — an unrostered key answers `null`, and
// every caller below falls back to that class's own `unrecorded` entry. This is the one function
// standing between "names the cause plainly" and a machine writing a plausible sentence about a
// member's account (R141: refuse, never assume).
export function causeFor(classKey, causeKey) {
  const cls = verificationClass(classKey);
  if (!cls) return null;
  return cls.causes.find((c) => c.key === String(causeKey || "")) || null;
}
// The honest fallback for a class: its own declared `unrecorded`, resolved through the same lookup so
// a roster that lost that entry cannot leave this answering `undefined`.
export function unrecordedCause(classKey) {
  const cls = verificationClass(classKey);
  return cls ? causeFor(classKey, cls.unrecorded) : null;
}
// A cause a class really has, or that class's honest unrecorded one. One door, so no check below
// writes its own fallback and they cannot drift.
const resolved = (classKey, causeKey) => causeFor(classKey, causeKey) || unrecordedCause(classKey);

const answer = (classKey, verdict, causeKey, detail = "") => ({
  class: classKey,
  label: verificationClass(classKey)?.label || classKey,
  verdict,
  cause: verdict === "not-reproducible" ? null : resolved(classKey, causeKey),
  detail: String(detail || ""),
});

// ---------------------------------------------------------------------------
// THE FOUR CHECKS. Every one of them can answer all THREE verdicts — a check structurally unable to
// say `confirmed` is a check that cannot fail (R164's tautology ruling), and one unable to say
// `cannot-tell` is the defect this whole round is about.
//
// **ABSENT EVIDENCE IS `cannot-tell` IN EVERY ONE OF THEM, AND NEVER `not-reproducible`.** "I could
// not look" and "it agrees" are different facts (R18/R19), and only one of them may ever be written
// under a member's name.
// ---------------------------------------------------------------------------

// PUSH. The rows are DT-4a's chain and the cause is DT-2b's endpoint evidence, both read rather than
// re-derived (R28/R29: a second reading of "did her phone answer" is a second answer key).
export function checkPush(delivery, endpoint = null) {
  if (!delivery || typeof delivery !== "object" || !Array.isArray(delivery.rows)) {
    return answer("push", "cannot-tell", "unknown", "no beacon chain was read for her");
  }
  const summary = memberDelivery(delivery.rows, {
    graceMinutes: delivery.graceMinutes, now: delivery.now,
  });
  // **NOTHING SENT IS NOT A HEALTHY PHONE** — `memberDelivery`'s own ruling, and the note it already
  // publishes says exactly why this cannot be a clean bill.
  if (summary.state === "never-sent") {
    return answer("push", "cannot-tell", "unknown", "nothing has ever been sent to her, so nothing here can speak for her phone");
  }
  if (summary.state === "broken") {
    // The cause is the endpoint evidence's, and ONLY when that evidence really found a silence. A
    // `waiting` or `arrived` endpoint beside broken older rows names nothing rather than borrowing a
    // cause written about a different send.
    const key = endpoint && endpoint.state === "silent" ? endpoint.cause : null;
    return answer("push", "confirmed", key,
      `${summary.counts.silent} never arrived and ${summary.counts.drewNothing} never drew, of ${summary.total}`);
  }
  if (summary.state === "waiting") {
    return answer("push", "cannot-tell", "unknown", "every send is still inside the grace, so nothing has failed yet");
  }
  return answer("push", "not-reproducible", null, `${summary.counts.delivered} of ${summary.total} delivered`);
}

// THE ROOM. D-1/D-4's fault receipt, filed under her id.
export function checkFault(rows) {
  if (!Array.isArray(rows)) {
    return answer("fault", "cannot-tell", "unrecorded", "the fault log was not read for her");
  }
  if (!rows.length) return answer("fault", "not-reproducible", null, "no handler has thrown for her");
  const newest = rows[0] && typeof rows[0] === "object" ? rows[0] : {};
  const where = String(newest.route || "").trim();
  const what = String(newest.error || "").trim();
  return answer("fault", "confirmed", "room-fault",
    `${rows.length} fault row${rows.length === 1 ? "" : "s"}${where ? `, newest on ${where}` : ""}${what ? `: ${what}` : ""}`);
}

// ACCESS. **BOTH HALVES MUST BE REAL BOOLEANS** — R136/R137's lesson one payload over: `Number(null)`
// is 0 and a coerced absence reads exactly like a real answer, which here would tell her a member who
// paid is locked out on the strength of evidence nobody gathered.
export function checkAccess(access) {
  if (!access || typeof access !== "object") {
    return answer("access", "cannot-tell", "unrecorded", "her entitlements were not read");
  }
  const paid = access.paid;
  const member = access.isMember;
  if (typeof paid !== "boolean" || typeof member !== "boolean") {
    return answer("access", "cannot-tell", "unrecorded", "her paid record or her resolved entitlements are missing");
  }
  if (paid && !member) {
    return answer("access", "confirmed", "paid-not-recognised",
      `she has a paid record and the house resolved her as ${String(access.tier || "unknown")}`);
  }
  return answer("access", "not-reproducible", null,
    `paid ${paid ? "yes" : "no"}, resolved ${String(access.tier || "unknown")}`);
}

// HELD CONTENT. LR1 ruling 1 — the one class where the member CANNOT have the evidence.
export function checkHeld(rows) {
  if (!Array.isArray(rows)) {
    return answer("held", "cannot-tell", "unrecorded", "her held content was not read");
  }
  if (!rows.length) return answer("held", "not-reproducible", null, "nothing of hers is held");
  const reasons = [...new Set(rows.map((r) => String(r?.reason || "").trim()).filter(Boolean))];
  return answer("held", "confirmed", "content-held",
    `${rows.length} held${reasons.length ? ` (${reasons.join(", ")})` : ""}`);
}

// ---------------------------------------------------------------------------
// THE FOCUS AND THE ROLL-UP.
// ---------------------------------------------------------------------------
// Which classes her own words point at. **NOTHING MATCHED IS EVERY CLASS**, never none: a ticket this
// pass cannot read is exactly the one that must be checked widest.
export function focusClasses(text) {
  const s = String(text ?? "");
  const hit = VERIFICATION_CLASSES.filter((c) => c.words.test(s)).map((c) => c.key);
  return hit.length ? hit : VERIFICATION_CLASS_KEYS.slice();
}

// **THE RANKING IS THE ROUND'S OTHER HALF: `cannot-tell` OUTRANKS `not-reproducible`.** A ticket
// whose focused lanes came back one healthy and one unreadable is NOT not-reproducible, because the
// unreadable one is where her problem would be if it is real (R160: an unmeasured lane is not a
// small one).
export function rollUp(checks, focus = VERIFICATION_CLASS_KEYS) {
  const list = (Array.isArray(checks) ? checks : []).filter((c) => focus.includes(c?.class));
  // Nothing focused at all is not a clean ticket either. This is unreachable through `verifyTicket`
  // (`focusClasses` never answers empty) and is held anyway, because a roll-up that answers
  // "not-reproducible" over an empty list is R43's M8 wearing a verdict.
  if (!list.length) return "cannot-tell";
  return list.reduce((worst, c) => (rankOf(c.verdict) > rankOf(worst) ? c.verdict : worst), "not-reproducible");
}

// ---------------------------------------------------------------------------
// THE PASS. Pure: evidence in, verdict out, and `now` never read from a clock in here.
// ---------------------------------------------------------------------------
export function verifyTicket({ text = "", evidence = null } = {}) {
  const e = evidence && typeof evidence === "object" ? evidence : {};
  const focus = focusClasses(text);
  const checks = [
    checkPush(e.delivery, e.endpoint),
    checkFault(e.faults),
    checkAccess(e.access),
    checkHeld(e.held),
  ];
  const verdict = rollUp(checks, focus);
  const focused = checks.filter((c) => focus.includes(c.class));
  return {
    verdict,
    focus,
    checks,
    // The causes she is meant to read first: the focused lanes that really found something.
    causes: focused.filter((c) => c.verdict === "confirmed").map((c) => c.cause).filter(Boolean),
    // **WHAT COULD NOT BE READ IS NAMED RATHER THAN COUNTED** (R17/R160 — no silent caps). A ticket
    // reported `cannot-tell` with nothing named would be an instrument shrugging.
    unmeasured: focused.filter((c) => c.verdict === "cannot-tell").map((c) => c.class),
    // Context only, and deliberately carried NOWHERE NEAR the verdict: a member's counts cannot
    // confirm or refute anything, and a check built out of them could never answer `confirmed`,
    // which is a check that cannot fail (R164).
    facts: e.facts && typeof e.facts === "object" ? e.facts : null,
  };
}

// ---------------------------------------------------------------------------
// TS1(3) — THE FIX LINE.
//
// HER CLAUSE, VERBATIM: *"(a) known safe repairs performed directly from the EXISTING repair
// vocabulary, nothing novel; (b) code-class issues become a trace-first runbook order in the house
// format with the member linked, and the member is told honestly; (c) nothing payment,
// account-ownership or deletion class is ever agent-actioned."*
//
// **A REPAIR IS DERIVED FROM THE CAUSE, NEVER CHOSEN (R31), AND THAT IS THE WHOLE SAFETY DESIGN.**
// The verification above already names the cause off her own rows; what a cause is worth doing about
// is a property OF THAT CAUSE, declared once, so nothing downstream — no route, no control, no
// request body — ever gets to say which repair to run on somebody's account.
//
// **AND THE VOCABULARY IS THE EXISTING ONE, WHICH IS THE OTHER HALF OF HER SENTENCE.** Every
// disposition below is a fact this house had already written down: `DELIVERY_CAUSES[].act` divides
// the push causes into the ones that heal themselves and the ones that are ours; `HELD_CAUSES` says
// out loud that lifting a hold is a tap in the room's queue; and the one real repair —
// `invalidateEntitlements` — is named in `ACCESS_CAUSES`' own act. Nothing here is novel.
// ---------------------------------------------------------------------------
export const FIX_DISPOSITIONS = [
  {
    key: "safe-repair",
    label: "A known repair",
    note: "There is an existing, safe, reversible-by-doing-nothing repair for this exact cause, and it may be performed from her seat.",
  },
  {
    key: "code-class",
    label: "This becomes an order",
    // (b). The vehicle already exists and is `composeDraft` — trace-first, in the house format, with
    // the member linked — so a code-class cause is not a new mechanism, it is the one this house has
    // had since A1: TRACE FIRST, REPORT BEFORE REPAIR.
    note: "Nothing may be repaired from a seat. It is a trace, and the drafted order on this ticket is where it goes.",
  },
  {
    key: "self-healing",
    label: "Resolves itself",
    note: "The house has already written down that this one ends without anybody doing anything, and doing something would be the defect.",
  },
  {
    key: "hers",
    label: "Yours to lift",
    // The room's queue is where a hold is lifted, and it is one tap. An agent lifting a hold is an
    // agent overruling the community gate, which is not a repair.
    note: "This is a decision rather than a repair, and it is made where the decision lives.",
  },
  {
    key: "none",
    label: "Nothing to act on",
    note: "The cause could not be named, so there is nothing here to act on. A repair chosen without a cause is a guess about somebody's account.",
  },
];
export const FIX_DISPOSITION_KEYS = FIX_DISPOSITIONS.map((d) => d.key);
export const dispositionFor = (k) =>
  FIX_DISPOSITIONS.find((d) => d.key === String(k || "")) || null;

// **THE EXISTING REPAIR VOCABULARY. ONE ENTRY, AND THAT IS THE POINT** — her clause says *nothing
// novel*, so this roster may only ever hold repairs this house already performs somewhere else.
export const REPAIRS = [
  {
    key: "drop-entitlement-cache",
    label: "Drop her cached entitlements",
    // Named by `ACCESS_CAUSES`' own act, and performed by `invalidateEntitlements`, which the Stripe
    // webhook and all three grant routes already call for exactly this reason.
    what: "The house forgets what it last worked out about her and re-resolves it from her own rows on her next request.",
    // WHY IT IS SAFE, which is the only reason a repair may be performed at all rather than ordered.
    safe: "It deletes one cache entry and nothing else: nothing of hers is written, nothing is destroyed, and doing it twice is doing it once.",
    ledger: "the entitlement cache was dropped for her",
  },
];
export const REPAIR_KEYS = REPAIRS.map((r) => r.key);
export const repairFor = (k) => REPAIRS.find((r) => r.key === String(k || "")) || null;

// **WHAT EACH CAUSE IS WORTH DOING ABOUT, DECLARED ONCE AND HELD AS A BICONDITIONAL** against the
// union of all four class rosters (R28/R29/R119/R134: named, never counted). A cause added to any
// roster tomorrow arrives with a disposition or goes red; an entry naming a cause nobody rosters is
// red too. Keys are CLASS-SCOPED, because `unrecorded` is a cause on three different rosters and
// `unknown` on a fourth, and they do not all mean the same thing.
export const fixKey = (classKey, causeKey) => `${String(classKey || "")}:${String(causeKey || "")}`;
export const FIX_BY_CAUSE = {
  // Push. The division is `DELIVERY_CAUSES[].act`'s own, read rather than re-decided.
  "push:endpoint-gone": { disposition: "self-healing", repair: null },
  "push:grant-gone": { disposition: "self-healing", repair: null },
  "push:worker-not-adopted": { disposition: "self-healing", repair: null },
  "push:never-accepted": { disposition: "code-class", repair: null },
  "push:worker-silent": { disposition: "code-class", repair: null },
  "push:unknown": { disposition: "none", repair: null },
  // The room. D-1/D-4's fault row is a trace, and a trace is an order.
  "fault:room-fault": { disposition: "code-class", repair: null },
  "fault:unrecorded": { disposition: "none", repair: null },
  // Access. The one cause in the house with a known safe repair behind it.
  "access:paid-not-recognised": { disposition: "safe-repair", repair: "drop-entitlement-cache" },
  "access:unrecorded": { disposition: "none", repair: null },
  // Held content. LR1 ruling 1 — the hold is a decision, and it is hers.
  "held:content-held": { disposition: "hers", repair: null },
  "held:unrecorded": { disposition: "none", repair: null },
};

// One cause, one fix. **REFUSES rather than composes**, exactly as `causeFor` does: an unrostered
// class, an unrostered cause, a missing row, an unrostered disposition and a repair key nobody
// declared all answer `null`, and every caller reads that as "nothing to act on" (R141).
export function fixFor(classKey, causeKey) {
  const cause = causeFor(classKey, causeKey);
  if (!cause) return null;
  const key = fixKey(classKey, causeKey);
  // The roster is asked by OWN PROPERTY before the lookup (R183): `FIX_BY_CAUSE["constructor"]` is a
  // truthy non-row, and a plan built out of it would be a function wearing a disposition.
  //
  // **AND THE PAIR IS NAMED RATHER THAN LEFT LOOKING LOAD-BEARING** (R193/R200/R201): every key here
  // is `class:cause` and `causeFor` above has already refused any cause no roster carries, so no
  // input can reach this guard today. It is defence in depth, and a session repairing the line above
  // must know it will not be caught here — which is exactly the cost of defence in depth that
  // nobody usually writes down.
  const row = Object.prototype.hasOwnProperty.call(FIX_BY_CAUSE, key) ? FIX_BY_CAUSE[key] : null;
  if (!row || typeof row !== "object") return null;
  const disposition = dispositionFor(row.disposition);
  if (!disposition) return null;
  const repair = row.repair ? repairFor(row.repair) : null;
  // A row naming a repair that is not on the roster is a row that names nothing. Refuse the whole
  // fix rather than answering with a disposition whose repair is missing.
  if (row.repair && !repair) return null;
  return { class: classKey, cause, disposition, repair };
}

// THE PLAN. Built from the verification's own CONFIRMED, FOCUSED lanes and nothing else — a cause
// that was not confirmed is not a cause, and a lane her words did not point at is not this ticket.
//
// **WHAT COULD NOT BE CLASSIFIED IS NAMED RATHER THAN DROPPED** (R17/R160 — no silent caps): a
// confirmed cause with no fix row would otherwise vanish out of the plan, and a plan that is silent
// about a cause reads exactly like a ticket with nothing in it (R8-4).
export function fixPlan(result) {
  const checks = Array.isArray(result?.checks) ? result.checks : [];
  const focus = Array.isArray(result?.focus) && result.focus.length
    ? result.focus : VERIFICATION_CLASS_KEYS;
  const rows = [];
  const unclassified = [];
  for (const c of checks) {
    if (!c || c.verdict !== "confirmed" || !focus.includes(c.class) || !c.cause) continue;
    const fix = fixFor(c.class, c.cause.key);
    if (fix) rows.push({ ...fix, label: c.label, detail: String(c.detail || "") });
    else unclassified.push({ class: c.class, cause: String(c.cause.key || "") });
  }
  const repairs = [];
  for (const r of rows) {
    if (r.disposition.key !== "safe-repair" || !r.repair) continue;
    if (!repairs.some((x) => x.key === r.repair.key)) repairs.push(r.repair);
  }
  return {
    rows,
    // (a) what may be performed now, from the existing vocabulary.
    repairs,
    // (b) what becomes a trace-first order instead.
    orders: rows.filter((r) => r.disposition.key === "code-class"),
    waiting: rows.filter((r) => r.disposition.key === "self-healing"),
    hers: rows.filter((r) => r.disposition.key === "hers"),
    unclassified,
  };
}

// ---------------------------------------------------------------------------
// THE SENTENCES. Owner-facing, and composed by the LAW rather than by a route, because D2's ruling is
// that the confirm a member of staff reads must describe what the SERVER will really do.
// ---------------------------------------------------------------------------
export function repairConfirmSentence({ memberName = "", repair = null, cause = null } = {}) {
  const r = repair && typeof repair === "object" ? repair : null;
  if (!r) return "";
  const who = String(memberName || "").trim() || "this member";
  const because = cause && cause.label ? ` Her account shows: ${cause.label}.` : "";
  return `${r.label} for ${who}?${because} ${r.what} ${r.safe}`;
}

// What the server says AFTER it acted (R66: an act that landed is not silent, and one that did not
// says so). A repair that did not run names no consequence, because there is none.
export function repairOutcome({ done, repair = null, memberName = "" } = {}) {
  const r = repair && typeof repair === "object" ? repair : null;
  const who = String(memberName || "").trim() || "They";
  if (!done) return "Nothing was repaired, so nothing about her account has changed.";
  const what = r ? r.ledger : "the repair ran";
  return `Done: ${what}. ${who} is resolved fresh on her next request.`;
}

// WHY A REPAIR WAS REFUSED. A roster rather than a lookup object, so an unrostered key answers "" and
// never something off `Object.prototype` (R183), and so no route composes a refusal at a call site.
export const REPAIR_REFUSALS = [
  {
    key: "not-agent-actionable",
    // (c), and it is the first thing the route asks — above every read of her account.
    sentence: "Nothing here may be repaired automatically. Payment, account-ownership and deletion tickets are never acted on by anything but you, and this one is not in the technical lane.",
  },
  {
    key: "no-account",
    sentence: "There is no account behind this message, so there is nothing of hers to repair.",
  },
  {
    key: "not-confirmed",
    sentence: "Nothing was confirmed on her account just now, so there is nothing here to repair.",
  },
  {
    key: "no-repair",
    sentence: "What was confirmed has no known safe repair. It is yours to lift, an order to trace, or it resolves itself — the lanes above say which.",
  },
];
export const REPAIR_REFUSAL_KEYS = REPAIR_REFUSALS.map((r) => r.key);
export const repairRefusalSentence = (k) =>
  REPAIR_REFUSALS.find((r) => r.key === String(k || ""))?.sentence || "";

// The one line at the top of the panel. Owner-facing, derived from the verdict roster so a fifth
// verdict cannot arrive without words (R31), and it names the unmeasured lanes because that is the
// half a verdict alone loses.
export function verificationHeadline(result) {
  const v = verdictFor(result?.verdict);
  if (!v) return "";
  const lanes = Array.isArray(result?.unmeasured) ? result.unmeasured : [];
  const named = lanes.map((k) => verificationClass(k)?.label || k).join(", ");
  return lanes.length ? `${v.label}. Not read: ${named}.` : `${v.label}.`;
}
